import React, { useCallback, useState } from 'react';
import EditIcon from '@material-ui/icons/Edit';
import AddIcon from '@material-ui/icons/Add';
import Nexticon from '@material-ui/icons/NavigateNext';
import DeleteIcon from '@material-ui/icons/Delete';
import RefreshIcon from '@material-ui/icons/Cached';
import {
  parseEntityRef,
  stringifyEntityRef,
  UserEntity,
  GroupEntity,
  DEFAULT_NAMESPACE,
  CompoundEntityRef,
} from '@backstage/catalog-model';
import {
  Avatar,
  Link,
  OverflowTooltip,
  Progress,
} from '@backstage/core-components';
import { alertApiRef, useApi } from '@backstage/core-plugin-api';
import { ANNOTATION_LOCATION } from '@backstage/catalog-model';
import {
  catalogApiRef,
  EntityRefLink,
  useEntity,
} from '@backstage/plugin-catalog-react';
import { VisibleRoles, RoleType } from '@internal/open-catalog';
import { scaffolderApiRef } from '@backstage/plugin-scaffolder-react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
  IconButton,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Checkbox,
} from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import useAsync from 'react-use/esm/useAsync';
import { Autocomplete } from '@material-ui/lab';
import TextField from '@material-ui/core/TextField';
import { usePermission } from '@backstage/plugin-permission-react';
import {
  catalogEntityDeletePermission,
  catalogEntityReadPermission,
  catalogEntityRefreshPermission,
} from '@backstage/plugin-catalog-common/alpha';
import { taskCreatePermission } from '@backstage/plugin-scaffolder-common/alpha';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    card: {
      border: `1px solid ${theme.palette.divider}`,
      boxShadow: theme.shadows[2],
      borderRadius: '4px',
      overflow: 'visible',
      position: 'relative',
      margin: theme.spacing(4, 1, 1),
      flex: '1',
      minWidth: '0px',
    },
    avatar: {
      position: 'absolute',
      top: '-2rem',
    },
  }),
);

const MemberComponent = (props: { member: UserEntity }) => {
  const classes = useStyles();
  const catalogApi = useApi(catalogApiRef);

  const {
    metadata: { name: metaName, description },
    spec: { profile },
  } = props.member;

  const displayName = profile?.displayName ?? metaName;

  const { loading, value: role } = useAsync(async () => {
    if (!props.member.relations) return '';

    const groupNames = props.member.relations.map(
      relation => parseEntityRef(relation.targetRef).name,
    );

    const roleGroups = await catalogApi.getEntities({
      filter: {
        kind: 'Group',
        'metadata.name': groupNames,
        'spec.type': 'role',
      },
    });

    return roleGroups.items.length
      ? roleGroups.items
          .filter(group =>
            VisibleRoles.includes(
              (group as GroupEntity).metadata?.name as RoleType,
            ),
          )
          .map(group => (group as GroupEntity).spec?.profile?.displayName)
          .filter(Boolean)
          .join(', ')
      : '';
  }, [catalogApi, props.member]);

  return (
    <Box className={classes.card}>
      <Box
        display="flex"
        flexDirection="column"
        m={3}
        alignItems="center"
        justifyContent="center"
      >
        <Avatar
          displayName={displayName}
          picture={profile?.picture}
          classes={classes}
        />
        <Box pt={2} sx={{ width: '100%' }} textAlign="center">
          <Typography variant="h6">
            <EntityRefLink
              data-testid="user-link"
              entityRef={props.member}
              title={displayName}
            />
          </Typography>
          {profile?.email && (
            <Link to={`mailto:${profile.email}`}>
              <OverflowTooltip text={profile.email} />
            </Link>
          )}
          {description && (
            <Typography variant="subtitle2">{description}</Typography>
          )}
          {role && (
            <Typography
              variant="subtitle2"
              style={{
                fontWeight: 'bold',
                color: 'primary.main',
                backgroundColor: 'rgba(0, 0, 255, 0.1)',
                borderRadius: '4px',
                padding: '4px 8px',
                display: 'inline-block',
                marginTop: '8px',
              }}
            >
              {loading ? 'Loading...' : role}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

const useListStyles = makeStyles(theme => ({
  divider: {
    marginTop: '1em',
  },
  memberList: {
    display: 'grid',
    gap: theme.spacing(1),
    gridTemplateColumns: `repeat(auto-fit, minmax(auto, ${theme.spacing(
      34,
    )}px))`,
  },
}));

export const EntityTeamCard = (props: {
  entityRef?: CompoundEntityRef;
  showRefreshButton?: boolean;
}) => {
  const { entityRef: compoundEntity, showRefreshButton } = props;
  const { entity: fetchedEntity } = useEntity<GroupEntity>();

  const classes = useListStyles();
  const catalogApi = useApi(catalogApiRef);
  const alertApi = useApi(alertApiRef);
  const scaffolderApi = useApi(scaffolderApiRef);

  const { value: fetchedFromApi, loading } = useAsync(async () => {
    if (!compoundEntity) return null;
    return (await catalogApi.getEntityByRef(compoundEntity)) as GroupEntity;
  }, [catalogApi, compoundEntity]);

  const entity = fetchedFromApi || fetchedEntity;

  // getEntitySourceLocation
  const {
    metadata: { name: groupName, namespace: grpNamespace },
  } = entity;
  const groupNamespace = grpNamespace || DEFAULT_NAMESPACE;

  // Use permissions
  const readPermission = usePermission({
    permission: catalogEntityReadPermission,
    resourceRef: stringifyEntityRef(entity),
  }).allowed;
  const deletePermission = usePermission({
    permission: catalogEntityDeletePermission,
    resourceRef: stringifyEntityRef(entity),
  }).allowed;
  const refreshPermission = usePermission({
    permission: catalogEntityRefreshPermission,
    resourceRef: stringifyEntityRef(entity),
  }).allowed;
  const scaffolderPermission = usePermission({
    permission: taskCreatePermission,
  }).allowed;

  const disabled =
    !readPermission ||
    !refreshPermission ||
    !scaffolderPermission ||
    !deletePermission;

  // console.log('allowed', allowed);
  const [modalStep, setModalStep] = useState(1);
  const handleNextStep = () => setModalStep(2);
  const handleBackStep = () => setModalStep(1);

  const { value: initialMembers } = useAsync(async () => {
    if (!entity) return [];

    const members = await catalogApi.getEntities({
      filter: {
        kind: 'User',
        ['relations.memberOf']: [
          stringifyEntityRef({
            kind: 'group',
            namespace: groupNamespace.toLocaleLowerCase('en-US'),
            name: groupName.toLocaleLowerCase('en-US'),
          }),
        ],
      },
    });

    return members.items as UserEntity[];
  }, [catalogApi, entity]);

  const [members, setMembers] = useState<UserEntity[]>([]);
  const [removedUsers, setRemovedUsers] = useState<string[]>([]);

  React.useEffect(() => {
    if (initialMembers) {
      setMembers(initialMembers);
    }
  }, [initialMembers]);

  const { value: allUsers } = useAsync(async () => {
    const allUsersList = await catalogApi.getEntities({
      filter: { kind: 'User' },
    });
    const existingMembersList = members;
    const existingMemberNames = new Set(
      existingMembersList.map(member => member.metadata.name),
    );
    return allUsersList.items.filter(
      user => !existingMemberNames.has(user.metadata.name),
    ) as UserEntity[];
  }, [catalogApi, members]);

  const [open, setOpen] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);

  const refreshEntity = useCallback(async () => {
    await catalogApi.refreshEntity(stringifyEntityRef(entity));
    alertApi.post({
      message: 'Refresh scheduled. Changes will be reflected shortly.',
      severity: 'info',
      display: 'transient',
    });
  }, [catalogApi, alertApi, entity]);

  const handleEditClick = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setModalStep(1);
    setRemovedUsers([]);
    setSelectedUsers([]);
    setOpen(false);
  };

  const handleRemoveUser = (userName: string) => {
    setRemovedUsers(prev => [...prev, userName]);
  };

  const handleConfirm = async () => {
    try {
      const managedByLocationPath =
        entity.metadata.annotations?.[ANNOTATION_LOCATION] ?? '/';

      const payload = {
        templateRef: 'default/group-management',
        values: {
          addedUsers: selectedUsers,
          removedUsers: removedUsers,
          groupEntityRef: stringifyEntityRef(entity),
          managedByLocationPath,
        },
      };

      alertApi.post({
        message: 'Updating group members, please wait...',
        severity: 'info',
        display: 'transient',
      });

      const { taskId } = await scaffolderApi.scaffold(payload);

      handleClose();
      refreshEntity();

      window.location.href = `/create/tasks/${taskId}`;
    } catch (err) {
      alertApi.post({
        message:
          'An unexpected error occurred while updating group members. Please contact SRE team for assistance.',
        severity: 'error',
        display: 'transient',
      });
    }
  };

  const handleCreateUser = () => {
    window.location.href = '/create/templates/default/user-management';
  };

  const usersToAdd =
    allUsers?.filter(user => selectedUsers.includes(user.metadata.name)) ?? [];

  const usersToRemove =
    members?.filter(user => removedUsers.includes(user.metadata.name)) ?? [];

  const removedUserEntities =
    members?.filter(member => removedUsers.includes(member.metadata.name)) ??
    [];

  return (
    <Card>
      <CardHeader
        title="Members"
        action={
          <>
            {showRefreshButton && (
              <IconButton
                aria-label="refresh"
                onClick={refreshEntity}
                disabled={!refreshPermission}
              >
                <RefreshIcon />
              </IconButton>
            )}
            <IconButton
              aria-label="edit"
              onClick={handleEditClick}
              disabled={disabled}
            >
              <EditIcon />
            </IconButton>
          </>
        }
      />
      <Divider className={classes.divider} />
      {loading ? (
        <Progress />
      ) : (
        <CardContent>
          <Box className={classes.memberList}>
            {members?.length ? (
              members.map(member => (
                <MemberComponent
                  key={stringifyEntityRef(member)}
                  member={member}
                />
              ))
            ) : (
              <Typography>The owner group has no members.</Typography>
            )}
          </Box>
        </CardContent>
      )}

      {/* Edit Members Modal */}
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>
          {modalStep === 1 ? 'Edit Members' : 'Confirm Changes'}
        </DialogTitle>
        <DialogContent>
          {modalStep === 1 ? (
            // Step 1: Select Users
            <>
              <Box mt={2}>
                <Typography variant="h6" gutterBottom>
                  Current Members
                </Typography>
                <Box
                  sx={{
                    maxHeight: 500,
                    overflow: 'auto',
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                  }}
                >
                  <List dense>
                    {members
                      ?.filter(
                        member => !removedUsers.includes(member.metadata.name),
                      )
                      .map(member => (
                        <ListItem key={member.metadata.name} divider>
                          <ListItemText
                            primary={
                              member.spec.profile?.displayName ||
                              member.metadata.name
                            }
                            secondary={member.spec.profile?.email}
                          />
                          <ListItemSecondaryAction>
                            <IconButton
                              edge="end"
                              onClick={() =>
                                handleRemoveUser(member.metadata.name)
                              }
                            >
                              <DeleteIcon />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                  </List>
                </Box>
              </Box>
              <Box mt={4}>
                <Typography variant="h6" gutterBottom>
                  Add Members
                </Typography>
                <Autocomplete
                  multiple
                  options={allUsers || []}
                  getOptionLabel={user =>
                    user.spec.profile?.displayName || user.metadata.name
                  }
                  value={
                    allUsers?.filter(user =>
                      selectedUsers.includes(user.metadata.name),
                    ) || []
                  }
                  onChange={(_, newValue) => {
                    setSelectedUsers(newValue.map(user => user.metadata.name));
                  }}
                  disableCloseOnSelect
                  getOptionDisabled={option =>
                    removedUsers.includes(option.metadata.name)
                  } // Optional
                  renderInput={params => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label="Search Users"
                      placeholder="Start typing to search..."
                      fullWidth
                    />
                  )}
                  renderOption={(option, { selected }) => (
                    <>
                      <Checkbox style={{ marginRight: 8 }} checked={selected} />
                      <Box>
                        <Typography variant="body1">
                          {option.spec.profile?.displayName ||
                            option.metadata.name}
                        </Typography>
                        {option.spec.profile?.email && (
                          <Typography variant="caption" color="textSecondary">
                            {option.spec.profile.email}
                          </Typography>
                        )}
                      </Box>
                    </>
                  )}
                />
              </Box>
              <Box mt={3}>
                <Typography variant="h6" gutterBottom>
                  Members to remove
                </Typography>
                <Box sx={{
                       border: '1px solid #e0e0e0', // cor e espessura do contorno
                       borderRadius: 1,              // opcional: bordas arredondadas
                       padding: 2,                   // opcional: espaçamento interno
                       minHeight: 50,               // altura minima para rolagem
                     }}
                >

                  {removedUserEntities.map(user => (
                    <Box
                      key={user.metadata.name}
                      display="flex"
                      alignItems="center"
                      mt={0.5}
                    >
                      <Typography variant="body2" style={{ flex: 1 }}>
                        {user.spec.profile?.displayName || user.metadata.name}
                      </Typography>
                      <IconButton
                        edge="end"
                        onClick={() => {
                          setRemovedUsers(prev =>
                            prev.filter(name => name !== user.metadata.name),
                          );
                        }}
                        size="small"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              </Box>
            </>
          ) : (
            // Step 2: Confirmation Screen
            <>
              <Box p={3} textAlign="center">
                <Typography variant="body2" color="textSecondary">
                  You’re about to update this group’s members. Review the list
                  below before confirming.
                </Typography>
              </Box>

              <Divider />

              <Box p={3}>
                {/* Users to Add */}
                {usersToAdd.length > 0 && (
                  <Box mb={3}>
                    <Typography
                      variant="subtitle1"
                      color="primary"
                      gutterBottom
                    >
                      Users to Add
                    </Typography>
                    <List dense>
                      {usersToAdd.map(user => (
                        <ListItem key={`add-${user.metadata.name}`} divider>
                          <ListItemText
                            primary={
                              user.spec.profile?.displayName ||
                              user.metadata.name
                            }
                            secondary={user.spec.profile?.email}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}

                {/* Users to Remove */}
                {usersToRemove.length > 0 && (
                  <Box mb={3}>
                    <Typography variant="subtitle1" color="error" gutterBottom>
                      Users to Remove
                    </Typography>
                    <List dense>
                      {usersToRemove.map(user => (
                        <ListItem key={`remove-${user.metadata.name}`} divider>
                          <ListItemText
                            primary={
                              user.spec.profile?.displayName ||
                              user.metadata.name
                            }
                            secondary={user.spec.profile?.email}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}

                {/* No changes fallback */}
                {usersToAdd.length === 0 && usersToRemove.length === 0 && (
                  <Box textAlign="center" mt={2}>
                    <Typography variant="body2" color="textSecondary">
                      No changes have been made.
                    </Typography>
                  </Box>
                )}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          {modalStep === 1 ? (
            <>
              <Button onClick={handleClose} color="secondary">
                Cancel
              </Button>
              <Button
                onClick={handleCreateUser}
                disabled={disabled}
                color="primary"
                startIcon={<AddIcon />}
              >
                Create New User
              </Button>
              <Button
                onClick={handleNextStep}
                startIcon={<Nexticon />}
                color="primary"
                disabled={
                  selectedUsers.length === 0 && removedUsers.length === 0
                }
              >
                Next
              </Button>
            </>
          ) : (
            <>
              <Button onClick={handleBackStep} color="secondary">
                Back
              </Button>
              <Button
                onClick={handleConfirm}
                disabled={disabled}
                color="primary"
              >
                Confirm
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Card>
  );
};
